import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { registerSchema } from '../../shared/validation/schemas';
import type { RegisterFormData } from '../../shared/validation/schemas';
import { useAuth } from '../../modules/auth/hooks/useAuth';
import { AuthProviders } from '../../modules/auth/ui/AuthProviders';
import Button from '../../components/base/Button';

export default function Register() {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form para registro
  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const handleSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);
    
    if (data.password !== data.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setIsLoading(false);
      return;
    }

    try {
      await registerUser(data.email, data.password, data.name);
      navigate('/dashboard');
    } catch (err: unknown) {
      setError((err as Error).message || 'Error al crear la cuenta');
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div 
      className="min-h-screen flex items-center justify-center relative bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://readdy.ai/api/search-image?query=Ultra%20futuristic%20financial%20dashboard%20interface%20with%20holographic%20displays%2C%20neon%20blue%20and%20green%20glowing%20elements%2C%20advanced%20AI%20analytics%20screens%2C%20floating%20data%20visualizations%2C%20cyberpunk%20style%20financial%20technology%2C%20dark%20background%20with%20bright%20accent%20lights%2C%20modern%20glass%20surfaces%2C%20high-tech%20trading%20floor%20atmosphere%2C%20digital%20financial%20charts%20and%20graphs%2C%20sleek%20minimalist%20design%20with%20glowing%20borders&width=1920&height=1080&seq=register-futuristic&orientation=landscape')`
      }}
    >
      {/* Efectos de partículas animadas */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-emerald-400 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-400 rounded-full animate-ping opacity-40"></div>
        <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-emerald-300 rounded-full animate-bounce opacity-30"></div>
        <div className="absolute top-2/3 right-1/4 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-50"></div>
      </div>

      <div className="w-full max-w-md mx-auto px-6 relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: '"Pacifico", serif' }}>
            Invirtiendo
          </h1>
          <p className="text-emerald-300 text-lg">Tu futuro financiero comienza aquí</p>
        </div>

        {/* Formulario principal */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              Crear cuenta
            </h2>
            <p className="text-gray-300">
              Únete a la revolución financiera
            </p>
          </div>

          {/* Mostrar errores */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-400/50 text-red-300 rounded-lg backdrop-blur-sm">
              {error}
            </div>
          )}

          {/* Formulario de Registro */}
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Nombre completo
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-user-line text-gray-400"></i>
                </div>
                <input
                  {...form.register('name')}
                  type="text"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent backdrop-blur-sm"
                  placeholder="Tu nombre completo"
                />
              </div>
              {form.formState.errors.name && (
                <p className="mt-1 text-sm text-red-400">{form.formState.errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400"></i>
                </div>
                <input
                  {...form.register('email')}
                  type="email"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent backdrop-blur-sm"
                  placeholder="tu@email.com"
                />
              </div>
              {form.formState.errors.email && (
                <p className="mt-1 text-sm text-red-400">{form.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  {...form.register('password')}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent backdrop-blur-sm"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 hover:text-white transition-colors`}></i>
                </button>
              </div>
              {form.formState.errors.password && (
                <p className="mt-1 text-sm text-red-400">{form.formState.errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Confirmar contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  {...form.register('confirmPassword')}
                  type="password"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent backdrop-blur-sm"
                  placeholder="••••••••"
                />
              </div>
              {form.formState.errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{form.formState.errors.confirmPassword.message}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              size="lg"
              className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white font-semibold py-4 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creando cuenta...
                </div>
              ) : (
                <>
                  <i className="ri-user-add-line mr-2"></i>
                  Crear Cuenta
                </>
              )}
            </Button>
          </form>

          {/* Autenticación Social */}
          <AuthProviders />

          {/* Link a login */}
          <div className="mt-6 text-center">
            <p className="text-gray-300">
              ¿Ya tienes cuenta?
              <button
                type="button"
                onClick={() => navigate('/login')}
                disabled={isLoading}
                className="ml-1 text-emerald-400 hover:text-emerald-300 font-medium transition-colors cursor-pointer disabled:opacity-50"
              >
                Inicia sesión
              </button>
            </p>
          </div>
        </div>

        {/* Características destacadas */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div className="text-white/80">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-shield-check-line text-emerald-400 text-xl"></i>
            </div>
            <p className="text-xs">100% Seguro</p>
          </div>
          <div className="text-white/80">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-ai-generate text-blue-400 text-xl"></i>
            </div>
            <p className="text-xs">IA Avanzada</p>
          </div>
          <div className="text-white/80">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-rocket-line text-emerald-400 text-xl"></i>
            </div>
            <p className="text-xs">Fácil de usar</p>
          </div>
        </div>

        {/* Botón de volver */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            disabled={isLoading}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center mx-auto disabled:opacity-50"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Volver al inicio
          </button>
        </div>
      </div>
    </div>
  );
}